import autoAnimate from "@formkit/auto-animate";
import { edroneTranslate, getPageLanguage } from "vue-app/plugins/edroneTranslate/edroneTranslate";
import momentTZ from "moment-timezone";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  DTOEngagementMessageBasicInfo,
  DTOEngagementMessageParameterType,
  DTOEngagementParameterType,
  DTOEngagementType,
  DTOFlowMessageGroupParameter,
  DTOFlowMessageParameter,
  DTOFlowParameter,
} from "vue-app/api/dto";
import isBetween from "dayjs/plugin/isBetween";
import { NEWSLETTER_TAB_VALUES } from "vue-app/types/enums.ts";

export function isNumber(value: unknown): value is number {
  return typeof value === "number" && !Number.isNaN(value);
}

export const getUrlWithHttpProtocol = (url: string) => {
  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url;
  } else {
    return `https://${url}`;
  }
};

export const hasAccess = (requiredRole: string) => {
  const userRoles = Object.keys(window.EDRONE_CONTEXT.user.roles).filter(
    (key) => window.EDRONE_CONTEXT.user.roles[key]
  );
  return userRoles.some((role) => {
    return role === requiredRole;
  });
};

export const langToLocale = (lang: string) => {
  switch (lang) {
    case "pl":
    case "pl-PL":
      return "pl-PL";
    case "pt":
    case "pt-BR":
      return "pt-BR";
    case "en":
    default:
      return "en-US";
  }
};

export const showIntercomChat = () => {
  // @ts-ignore
  if (typeof Intercom === "undefined") {
    return;
  } else {
    // @ts-ignore
    // eslint-disable-next-line no-undef
    Intercom?.("show");
  }
};

export const hideIntercomChat = () => {
  // @ts-ignore
  if (typeof Intercom === "undefined") {
    return;
  } else {
    // @ts-ignore
    // eslint-disable-next-line no-undef
    Intercom?.("hide");
  }
};

export const showIntercomNewMessage = (message: string) => {
  // @ts-ignore
  if (typeof Intercom === "undefined") {
    return;
  } else {
    // @ts-ignore
    // eslint-disable-next-line no-undef
    Intercom?.("showNewMessage", message);
  }
};

export const updateIntercom = (key: string, value: string | number | boolean) => {
  // @ts-ignore
  if (typeof Intercom === "undefined") {
    return;
  } else {
    // @ts-ignore
    // eslint-disable-next-line no-undef
    Intercom?.("update", {
      [key]: value,
    });
  }
};

export function edroneAutoAnimate(htmlEl: HTMLElement) {
  autoAnimate(htmlEl, {
    duration: 320,
    easing: "ease-out",
  });
}

export const mapCurrencyToSign = (currency: string) => {
  switch (currency) {
    case "BRL":
      return "R$";
    case "CZK":
      return "Kč";
    case "EUR":
      return "€";
    case "GBP":
      return "£";
    case "PLN":
      return "zł";
    case "RON":
      return "lei";
    case "USD":
      return "$";
    default:
      return currency;
  }
};

export const mapSignToCurrency = (sign: string) => {
  const trimmedSign = sign.trim();
  switch (trimmedSign) {
    case "R$":
      return "BRL";
    case "Kč":
      return "CZK";
    case "€":
      return "EUR";
    case "£":
      return "GBP";
    case "zł":
      return "PLN";
    case "lei":
      return "RON";
    case "$":
      return "USD";
    default:
      return trimmedSign;
  }
};

export function compareAsc(value1: any, value2: any) {
  if (typeof value1 === "number" && typeof value2 === "number") {
    return value1 - value2;
  }
  if (typeof value1 === "string" && typeof value2 === "string") {
    return value1.localeCompare(value2);
  }
  if (typeof value1 === "boolean" && typeof value2 === "boolean") {
    return value1 === value2 ? 0 : value1 ? 1 : -1;
  }
  if (value1 instanceof Date && value2 instanceof Date) {
    console.log("date");
    return value1.getTime() - value2.getTime();
  }
  if (value1 instanceof Array && value2 instanceof Array) {
    console.log("array");
    return value1.length - value2.length;
  }
  if (value1 instanceof Object && value2 instanceof Object) {
    console.log("object");
    return Object.keys(value1).length - Object.keys(value2).length;
  }
  return 0;
}

export function compareDesc(value1: any, value2: any) {
  return -compareAsc(value1, value2);
}

export function detectIsFirefox() {
  return navigator.userAgent.toLowerCase().indexOf("firefox") > -1;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function $_trans(translationKey: string, translationParams = {}): any {
  // Here is "any" because adding the type "string" | "object" throws an error in almost every file withing code/control
  const lang = getPageLanguage();
  return edroneTranslate(window._trans, lang, translationKey, translationParams);
}

export function formatDate(date, timezone, isNumber) {
  let localDate = date;
  if (isNumber) {
    localDate = new Date(Number(date));
  }
  return momentTZ.utc(localDate).tz(timezone, true).format("YYYY-MM-DD HH:mm") + " " + timezone;
}

/**
 * Formats a date string from English format (e.g., "5th Sep") to localized format.
 *
 * @param dateString - Date string in format "5th Sep", "5 Sep", etc.
 * @param language - Language code ('en', 'pl', 'pt'). Defaults to 'en'.
 * @returns Formatted date string: "5 września" (pl), "5 September" (en), "5 de setembro" (pt)
 *
 * @example
 * formatDateWithLocale("5th Sep", "pl") // "5 września"
 * formatDateWithLocale("1st Jan", "en") // "1 January"
 * formatDateWithLocale("15th Dec", "pt") // "15 de dezembro"
 */
export function formatDateWithLocale(dateString: string, language: string = "en"): string {
  if (!dateString) return dateString;

  const monthNames = {
    en: [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ],
    pl: [
      "stycznia",
      "lutego",
      "marca",
      "kwietnia",
      "maja",
      "czerwca",
      "lipca",
      "sierpnia",
      "września",
      "października",
      "listopada",
      "grudnia",
    ],
    pt: [
      "janeiro",
      "fevereiro",
      "março",
      "abril",
      "maio",
      "junho",
      "julho",
      "agosto",
      "setembro",
      "outubro",
      "novembro",
      "dezembro",
    ],
  };

  const monthAbbr = {
    Jan: 0,
    Feb: 1,
    Mar: 2,
    Apr: 3,
    May: 4,
    Jun: 5,
    Jul: 6,
    Aug: 7,
    Sep: 8,
    Oct: 9,
    Nov: 10,
    Dec: 11,
  };

  // remove ordinal suffixes and extract day/month
  const cleanInput = dateString.replace(/(\d+)(st|nd|rd|th)/, "$1");
  const match = cleanInput.match(/(\d+)\s+(\w{3})/);

  if (!match) {
    return dateString;
  }

  const day = parseInt(match[1]);
  const monthAbbreviation = match[2];
  const monthIndex = monthAbbr[monthAbbreviation];

  if (monthIndex === undefined) {
    return dateString;
  }

  // Get month names for current language
  const months = monthNames[language] || monthNames.en;
  const monthName = months[monthIndex];

  // Format based on language: e.g. "5 de setembro" (Portuguese), "5 September" (English), "5 września" (Polish)
  if (language === "pt") {
    return `${day} de ${monthName}`;
  }
  return `${day} ${monthName}`;
}

export function getQueryParam(name) {
  const searchString = window.location.search;
  if (searchString === "") {
    return "";
  }

  const param = searchString
    .replace("?", "")
    .split("&")
    .map((part) => part.split("="))
    .find((param) => param[0] === name);

  return param ? param[1] : "";
}

export function byteLength(str: string) {
  // returns the byte length of an utf8 string
  let s = str.length;
  for (let i = str.length - 1; i >= 0; i--) {
    const code = str.charCodeAt(i);
    if (code > 0x7f && code <= 0x7ff) s++;
    else if (code > 0x7ff && code <= 0xffff) s += 2;
    if (code >= 0xdc00 && code <= 0xdfff) i--; //trail surrogate
  }
  return s;
}

export function getSafe(fn) {
  try {
    return fn();
  } catch (e) {}
}

/**
 * This function should be used whenever we need to display a date in the timezone of the account.
 * But remember **not to use** `.toDate()` because it return new Date() object with timezone of the browser.
 *
 * @param date Any date value that is accepted by dayjs.
 * Unix timestamp in **milliseconds** (not seconds!) is also accepted.
 */
export function dayjsWithTz(date?: dayjs.ConfigType) {
  dayjs.extend(utc);
  dayjs.extend(timezone);
  dayjs.extend(isBetween);

  if (date) {
    // If the date is a string without timezone info, treat it as UTC first
    let parsedDate;
    if (typeof date === "string" && !date.includes("Z") && !date.includes("+") && date.includes("T")) {
      // For ISO strings without timezone, parse as UTC first
      parsedDate = dayjs.utc(date).tz(window.EDRONE_CONTEXT.timezone);
    } else {
      parsedDate = dayjs.tz(date, window.EDRONE_CONTEXT.timezone);
    }
    return parsedDate;
  } else {
    return dayjs().tz(window.EDRONE_CONTEXT.timezone);
  }
}

export function formatLocalDateWithAccountTimezone(date: number | null) {
  if (date) {
    return formatLocalDate(new Date(date)) + " " + window.EDRONE_CONTEXT.timezone;
  }
  return "-";
}

export function formatTimeAgo(date: Date) {
  return dayjs(date).fromNow();
}

export function formatLocalDate(date: Date) {
  return dayjsWithTz(date).format("YYYY-MM-DD HH:mm");
}

export function formatDateWithMonthName(date: number | null): string {
  if (!date) return "-";
  const d = dayjsWithTz(new Date(date));
  const day = d.format("D");
  const year = d.format("YYYY");
  const currentYear = dayjsWithTz().format("YYYY");
  const time = d.format("HH:mm");
  const monthName = d.format("MMM");
  const atTranslations: Record<string, string> = {
    en: "at",
    pl: "o",
    es: "a las",
    pt: "às",
  };
  const lang = getPageLanguage() || "en";
  const atWord = atTranslations[lang] || atTranslations.en;
  if (year === currentYear) {
    return `${day} ${monthName} ${atWord} ${time}`;
  }
  return `${day} ${monthName} ${year} ${atWord} ${time}`;
}

export const parseDate = (date: string | number | Date, options?: { timestampFormat: "sec" | "ms" }): Date => {
  let parsedDate: Date;
  if (typeof date === "number") {
    if (options?.timestampFormat === "sec") {
      parsedDate = new Date(date * 1000); // convert to ms
    } else {
      parsedDate = new Date(date);
    }
  } else if (typeof date === "string") {
    parsedDate = new Date(date);
  } else if (date instanceof Date) {
    parsedDate = date;
  } else {
    throw new Error("Invalid date format");
  }
  return parsedDate;
};

export function formatTimeToHoursAndMinutes(
  date: string | number | Date,
  options?: { timestampFormat: "sec" | "ms" }
): string {
  const parsedDate = parseDate(date, options);
  const formatOptions: Intl.DateTimeFormatOptions = { hour: "2-digit", minute: "2-digit" };
  return new Intl.DateTimeFormat("pl-PL", formatOptions).format(parsedDate);
}

export function getTimezoneOffsetHours(date: Date = new Date()): number {
  return dayjsWithTz(date).utcOffset() / 60;
}

export function isValidDate(date: unknown) {
  dayjs.extend(utc);
  dayjs.extend(timezone);

  if (date === null || date === undefined) return false; // because dayjs returns true for undefined

  try {
    return dayjs(date as any).isValid();
  } catch (e) {
    return false;
  }
}

export function getUnix(date: Date) {
  dayjs.extend(utc);
  dayjs.extend(timezone);

  return dayjs(date).unix();
}

export function unixToMilliseconds(unix: number) {
  return unix * 1000;
}

export const getReadyFlowMessageGroupParameter = (
  messageGroupParameters: { [index: string]: { [index: string]: DTOFlowMessageGroupParameter[] } },
  groupId: number,
  orderIndex: number,
  parameterName: DTOEngagementMessageParameterType
) => {
  const parameter = messageGroupParameters[groupId][orderIndex].find((param) => param.type === parameterName);
  if (!parameter) {
    throw new Error(`Parameter "${parameterName}" not found at index ${orderIndex}.`);
  }
  return parameter;
};

export const getReadyFlowMessageParameter = (
  messageParameters: { [index: string]: DTOFlowMessageParameter[] },
  orderIndex: number,
  parameterName: DTOEngagementMessageParameterType
) => {
  const parameter = messageParameters[orderIndex].find((param) => param.type === parameterName);
  if (!parameter) {
    throw new Error(`Parameter "${parameterName}" not found at index ${orderIndex}.`);
  }
  return parameter;
};

export const getReadyFlowParameter = (parameters: DTOFlowParameter[], parameterName: DTOEngagementParameterType) => {
  const parameter = parameters.find((param) => param.type === parameterName);
  if (!parameter) {
    throw new Error(`Parameter "${parameterName}" not found.`);
  }
  return parameter;
};

export const getReadyFlowMessageByOrderIndex = (messages: DTOEngagementMessageBasicInfo[], orderIndex: number) => {
  const message = messages.find((message) => message.orderIndex === orderIndex && message.groupId === null);
  if (!message) {
    throw new Error(`Message not found at index ${orderIndex}.`);
  }
  return message;
};

export const getReadyFlowMessageByOrderIndexAndGroupIdAndChannelType = (
  messages: DTOEngagementMessageBasicInfo[],
  orderIndex: number,
  groupId: number,
  channelTypeId: number
) => {
  const message = messages.find(
    (message) =>
      message.orderIndex === orderIndex && message.groupId === groupId && message.channelTypeId === channelTypeId
  );
  if (!message) {
    throw new Error(
      `Message not found at index ${orderIndex} and groupId ${groupId} and channelTypeId ${channelTypeId}.`
    );
  }
  return message;
};

export const getReadyFlowMessageByChannelType = (messages: DTOEngagementMessageBasicInfo[], channelTypeId: number) => {
  const message = messages.find((message) => message.channelTypeId === channelTypeId);
  if (!message) {
    throw new Error(`Message not found at channelTypeId ${channelTypeId}.`);
  }
  return message;
};

export const getReadyFlowMessageByOrderIndexAndChannelType = (
  messages: DTOEngagementMessageBasicInfo[],
  orderIndex: number,
  channelTypeId: number
) => {
  const message = messages.find(
    (message) => (message.orderIndex || 0) === orderIndex && message.channelTypeId === channelTypeId
  );
  if (!message) {
    throw new Error(`Message not found at index ${orderIndex} and channelTypeId ${channelTypeId}.`);
  }
  return message;
};

export interface BasicProductData {
  title: string;
  price: number;
  oldPrice: number;
  image: string;
}

interface ParseEmailHtmlOptions {
  products?: BasicProductData[];
  includeFooterStyles?: boolean;
  includeAdditionalReplacements?: boolean;
  footerHtml?: string;
  bannerUrl?: string;
  aiBannerUrl?: string;
}

export const removeBrandingElement = (html: string, elementId: string): string => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const element = doc.getElementById(elementId);
  if (element) {
    element.remove();
  }
  // Return only the body innerHTML to avoid adding extra html/head tags
  return doc.body.innerHTML;
};

export const parseEmailHtml = (html: string | null, options: ParseEmailHtmlOptions = {}): string => {
  if (!html) {
    return "";
  }
  const {
    products = [],
    includeFooterStyles = true,
    includeAdditionalReplacements = true,
    footerHtml,
    bannerUrl,
    aiBannerUrl,
  } = options;

  const currencySign = mapCurrencyToSign(window.EDRONE_CONTEXT.user.currency || "USD");
  let parsedHtml = html;

  for (let i = 1; i <= 6; i++) {
    ["DYNAMIC_ITEM_", "BESTSELLER_ITEM_", "NEWEST_ITEM_", "RECOMMENDATED_ITEM_"].forEach((name) => {
      if (!parsedHtml.includes("*|" + name + i)) {
        return;
      }

      const product = products[i - 1];

      const price = product?.price ? new Intl.NumberFormat().format(product.price) : "79,99";
      const oldPrice = product?.oldPrice ? new Intl.NumberFormat().format(product.oldPrice) : "89,99";
      const title = product?.title || "Example title";
      const image = product?.image || "https://control.edrone.me/assets/newsletter-builder/_row_thumbs/1x-square.jpg";

      parsedHtml = parsedHtml.replaceAll("*|" + name + i + "_IMAGE_URL|*", image);
      parsedHtml = parsedHtml.replaceAll("*|" + name + i + "_TITLE|*", title);
      parsedHtml = parsedHtml.replaceAll("*|" + name + i + "_PRICE|*", price);
      parsedHtml = parsedHtml.replaceAll("*|" + name + i + "_OLD_PRICE|*", oldPrice);
      parsedHtml = parsedHtml.replaceAll("*|" + name + i + "_CURRENCY|*", currencySign);
      parsedHtml = parsedHtml.replaceAll("*|" + name + i + "_OLD_CURRENCY|*", currencySign);
    });
  }

  if (includeAdditionalReplacements) {
    parsedHtml = parsedHtml.replace(/\*\|IMAGE_WIDTH\|\*/g, "500");

    const fontSize = parsedHtml.match(/data-title-font-size="(\d+)"/);
    const height = fontSize ? Number(fontSize[1]) * 3 : 48;
    parsedHtml = parsedHtml.replace(/\*\|TITLE_HEIGHT\|\*/g, `${height}`);

    parsedHtml = parsedHtml.replace(/\*\|TRACKER_ID\|\*/g, String(window.EDRONE_CONTEXT.trackerId));
    parsedHtml = parsedHtml.replace(/\*\|CUSTOMER_EMAIL\|\*/g, String(window.EDRONE_CONTEXT.user.email));
    parsedHtml = parsedHtml.replace(/\*\|FRAME_UNSUBSCRIBE_TEXT\|\*/g, "Unsubscribe");
  }

  parsedHtml = parsedHtml.replace(".u-row .u-col img {", ".notUsedInPreview {");

  const baseStyles =
    ".product-title p {overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; line-clamp: 2; -webkit-box-orient: vertical;}";

  let additionalStyles = "";
  if (includeFooterStyles) {
    additionalStyles =
      " .u-row-footer--edrone .u-col-50 { width: 50% !important; max-width: 50% !important; min-width: 50% !important;}";
  }

  const responsiveStyles = " @media only screen and (max-width: 500px) { .product-title { height: auto !important } }";

  parsedHtml = parsedHtml.replace("</style>", `${responsiveStyles} ${baseStyles}${additionalStyles}</style>`);

  const isDev = window.EDRONE_CONTEXT.user?.isDev;

  if (isDev) {
    const logoUrl = window.EDRONE_CONTEXT.tracker?.trackerBranding?.logoUrl || "";
    parsedHtml = parsedHtml.replaceAll("*|BRANDING_LOGO|*", logoUrl);

    if (bannerUrl) {
      parsedHtml = parsedHtml.replaceAll("*|BRANDING_BANNER_URL|*", bannerUrl);
    }

    if (aiBannerUrl) {
      parsedHtml = parsedHtml.replaceAll("*|BRANDING_AI_BANNER_URL|*", aiBannerUrl);
    }

    // Remove header if useHeader is false
    if (!window.EDRONE_CONTEXT.tracker?.trackerBranding?.useHeader) {
      parsedHtml = removeBrandingElement(parsedHtml, "brandingHeader");
    }

    // Handle footer replacement - check if footer should be used
    const shouldUseFooter = window.EDRONE_CONTEXT.tracker?.trackerBranding?.useFooter ?? true;
    if (includeAdditionalReplacements && footerHtml && footerHtml.length > 0 && shouldUseFooter) {
      // Only add footer if the tag exists - don't append it at the end
      if (parsedHtml.includes("*|BRANDING_FOOTER|*")) {
        parsedHtml = parsedHtml.replaceAll("*|BRANDING_FOOTER|*", footerHtml);
      }
    } else {
      // Remove footer placeholder if footer is disabled or empty
      parsedHtml = parsedHtml.replaceAll("*|BRANDING_FOOTER|*", "");
      // Also remove any existing footer divs
      parsedHtml = removeBrandingElement(parsedHtml, "brandingFooter");
    }
  } else {
    // For non-dev users, remove all branding elements
    parsedHtml = parsedHtml.replaceAll("*|BRANDING_LOGO|*", "");
    parsedHtml = parsedHtml.replaceAll("*|BRANDING_BANNER_URL|*", "");
    parsedHtml = parsedHtml.replaceAll("*|BRANDING_AI_BANNER_URL|*", "");
    parsedHtml = parsedHtml.replaceAll("*|BRANDING_FOOTER|*", "");

    // Remove brandingHeader and brandingFooter divs
    parsedHtml = removeBrandingElement(parsedHtml, "brandingHeader");
    parsedHtml = removeBrandingElement(parsedHtml, "brandingFooter");
  }

  return parsedHtml;
};

export const replaceUnlayerTagToExampleValues = (
  html: string,
  _currency: string,
  footerHtml?: string,
  bannerUrl?: string,
  aiBannerUrl?: string
) => {
  return parseEmailHtml(html, {
    products: [],
    includeFooterStyles: false,
    includeAdditionalReplacements: true,
    footerHtml,
    bannerUrl,
    aiBannerUrl,
  });
};

export const getWaitText = (
  seconds: number,
  translation: {
    message: string;
    after: string;
    days: string;
    day: string;
    hours2: string;
    hours: string;
    hour: string;
    wait: string;
    and: string;
    weWait: string;
  },
  skipText?: boolean
) => {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);

  let daysText: string = "";
  if (days <= 0) {
    daysText = translation.days;
  } else if (days === 1) {
    daysText = translation.day;
  } else {
    daysText = translation.days;
  }
  let hoursText: string = "";
  if (hours <= 0) {
    hoursText = translation.hours;
  } else if (hours === 1) {
    hoursText = translation.hour;
  } else if (hours < 5) {
    hoursText = translation.hours2;
  } else {
    hoursText = translation.hours;
  }

  if (days === 0 && hours === 0) {
    return "";
  } else if (hours === 0) {
    return `${skipText ? "" : translation.weWait} ${days} ${daysText}`;
  } else if (days === 0) {
    return `${skipText ? "" : translation.weWait} ${hours} ${hoursText}`;
  } else {
    return `${skipText ? "" : translation.weWait} ${days} ${daysText} ${translation.and} ${hours} ${hoursText}`;
  }
};

export const isSupportedEngagementTypeForSmartSubject = (type: number | string) => {
  enum SupportedEngagementTypes {
    "RESTORE_CUSTOMERS" = 1,
    "ABANDONED_CARTS" = 5,
    "ADVANCED_RESTORE_CUSTOMERS" = 7,
    "AFTER_SALE" = 9,
    "RECOMMENDATIONS" = 15,
    "NEWSLETTER_SIGN_UP_AFTER_PURCHASE" = 23,
    "PRICE_CHANGE_UP" = 24,
    "PRICE_CHANGE_DOWN" = 25,
    "LOW_STOCK_PRODUCTS" = 26,
    "NEWEST_PRODUCTS" = 27,
    "ASK_FOR_REVIEW" = 28,
    "BACK_IN_STOCK" = 29,
  }

  return type in SupportedEngagementTypes || Object.values(SupportedEngagementTypes).includes(type as number);
};

export const isSupportedEngagementTypeForMultiChannel = (engagementType: DTOEngagementType) => {
  return [
    "ADVANCED_RESTORE_CUSTOMERS",
    "RESTORE_CUSTOMERS",
    "ABANDONED_CARTS",
    "BIRTHDAY",
    "AFTER_SALE",
    "BACK_IN_STOCK",
    "ASK_FOR_REVIEW",
    "NEWEST_PRODUCTS",
    "LOW_STOCK_PRODUCTS",
    "NEWSLETTER_SIGN_UP_AFTER_PURCHASE",
    "PRICE_CHANGE_DOWN",
    "PRICE_CHANGE_UP",
    "NEWSLETTER_SUBSCRIPTION",
    "LOYALTY_PROGRAM",
    "RECOMMENDATIONS",
    "SMS_SUBSCRIPTION",
  ].includes(engagementType);
};

export const convertPxToRem = (px: string): string => {
  if (Number.isNaN(Number(px))) {
    return "0rem";
  }
  return `${parseFloat(px) / 16}rem`;
};

export type FormatNumberStyle = "currency" | "percent" | "decimal" | "unit" | "pp";

export function formatNumberV2(
  value: string | number | null | undefined,
  options?: {
    currency?: string | null;
    style?: FormatNumberStyle;
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  }
) {
  const parsedValueToNumber = Number(value || 0);

  if (options?.style === "currency") {
    return formatPrice(parsedValueToNumber, options?.currency);
  }

  // "pp" is an alias for "percent" because Intl.NumberFormat does not support "pp" style
  // so we have handle pp as percent and then remove % from the result and add 'p.p' at the end
  const style = options?.style === "pp" ? "percent" : options?.style || "decimal";

  const fractionDigits =
    style === "percent"
      ? {
          minimumFractionDigits: options?.minimumFractionDigits || 1,
          maximumFractionDigits: options?.maximumFractionDigits || 2,
        }
      : undefined;

  const preparedValue = new Intl.NumberFormat(window.EDRONE_CONTEXT.user.lang, {
    style,
    ...fractionDigits,
  }).format(parsedValueToNumber);

  if (options?.style === "pp") {
    return preparedValue.replace("%", "") + " p.p";
  }

  return preparedValue;
}

export const formatPrice = (price: number, currency?: string | null) => {
  let preparedCurrency = currency || window.EDRONE_CONTEXT.user.currency;

  if (!preparedCurrency) {
    preparedCurrency = "USD";
  }
  preparedCurrency = mapSignToCurrency(preparedCurrency);

  return new Intl.NumberFormat(window.EDRONE_CONTEXT.user.lang, {
    style: "currency",
    currency: preparedCurrency,
  }).format(price);
};

export const getAutomationName = (typeId: number) => {
  switch (typeId) {
    case 1:
      return window._trans._readyFlows.automation.name.RESTORE_CUSTOMERS;
    case 2:
      return window._trans._readyFlows.automation.name.LOYALTY_PROGRAM;
    case 5:
      return window._trans._readyFlows.automation.name.ABANDONED_CARTS;
    case 6:
      return window._trans._readyFlows.automation.name.NEWSLETTER;
    case 7:
      return window._trans._readyFlows.automation.name.ADVANCED_RESTORE_CUSTOMERS;
    case 8:
      return window._trans._readyFlows.automation.name.SMS_NEWSLETTER;
    case 9:
      return window._trans._readyFlows.automation.name.AFTER_SALE;
    case 10:
      return window._trans._readyFlows.automation.name.NEWSLETTER_SUBSCRIPTION;
    case 11:
      return window._trans._readyFlows.automation.name.CUSTOM_NEWSLETTER;
    case 12:
      return window._trans._readyFlows.automation.name.WISH_LIST;
    case 13:
      return window._trans._readyFlows.automation.name.NEWSLETTER_AB;
    case 14:
      return window._trans._readyFlows.automation.name.NEWSLETTER_CATEGORIES;
    case 15:
      return window._trans._readyFlows.automation.name.RECOMMENDATIONS;
    case 16:
      return window._trans._readyFlows.automation.name.CROSS_SELLING_TRANSACTIONAL;
    case 17:
      return window._trans._readyFlows.automation.name.PUSH_NEWSLETTER;
    case 18:
      return window._trans._readyFlows.automation.name.CHAT_SUBSCRIPTION;
    case 19:
      return window._trans._readyFlows.automation.name.BIRTHDAY;
    case 20:
      return window._trans._readyFlows.automation.name.REFER_A_FRIEND;
    case 21:
      return window._trans._readyFlows.automation.name.CUSTOM_FLOW;
    case 22:
      return window._trans._readyFlows.automation.name.NEWSLETTER_HUB;
    case 23:
      return window._trans._readyFlows.automation.name.NEWSLETTER_SIGN_UP_AFTER_PURCHASE;
    case 24:
      return window._trans._readyFlows.automation.name.PRICE_CHANGE_UP;
    case 25:
      return window._trans._readyFlows.automation.name.PRICE_CHANGE_DOWN;
    case 26:
      return window._trans._readyFlows.automation.name.LOW_STOCK_PRODUCTS;
    case 27:
      return window._trans._readyFlows.automation.name.NEWEST_PRODUCTS;
    case 28:
      return window._trans._readyFlows.automation.name.ASK_FOR_REVIEW;
    case 29:
      return window._trans._readyFlows.automation.name.BACK_IN_STOCK;
    case 31:
      return window._trans._readyFlows.automation.name.SMS_SUBSCRIPTION;
    case 32:
      return window._trans._readyFlows.automation.name.REVIEW_REQUEST;
    default: {
      return null;
    }
  }
};

export const getAutomationDescription = (name: DTOEngagementType) => {
  switch (name) {
    case "RESTORE_CUSTOMERS":
      return window._trans._readyFlows.automation.description.RESTORE_CUSTOMERS;
    case "ABANDONED_CARTS":
      return window._trans._readyFlows.automation.description.ABANDONED_CARTS;
    case "AFTER_SALE":
      return window._trans._readyFlows.automation.description.AFTER_SALE;
    case "LOYALTY_PROGRAM":
      return window._trans._readyFlows.automation.description.LOYALTY_PROGRAM;
    case "ADVANCED_RESTORE_CUSTOMERS":
      return window._trans._readyFlows.automation.description.ADVANCED_RESTORE_CUSTOMERS;
    case "NEWSLETTER_SUBSCRIPTION":
      return window._trans._readyFlows.automation.description.NEWSLETTER_SUBSCRIPTION;
    case "WISH_LIST":
      return window._trans._readyFlows.automation.description.WISH_LIST;
    case "RECOMMENDATIONS":
      return window._trans._readyFlows.automation.description.RECOMMENDATIONS;
    case "CROSS_SELLING_TRANSACTIONAL":
      return window._trans._readyFlows.automation.description.CROSS_SELLING_TRANSACTIONAL;
    case "CHAT_SUBSCRIPTION":
      return window._trans._readyFlows.automation.description.CHAT_SUBSCRIPTION;
    case "BIRTHDAY":
      return window._trans._readyFlows.automation.description.BIRTHDAY;
    case "REFER_A_FRIEND":
      return window._trans._readyFlows.automation.description.REFER_A_FRIEND;
    case "NEWSLETTER":
    case "CUSTOM_NEWSLETTER":
    case "NEWSLETTER_AB":
    case "SMS_NEWSLETTER":
    case "NEWSLETTER_CATEGORIES":
      return "";
    case "NEWSLETTER_SIGN_UP_AFTER_PURCHASE":
      return window._trans._readyFlows.automation.description.NEWSLETTER_SIGN_UP_AFTER_PURCHASE;
    case "PRICE_CHANGE_UP":
      return window._trans._readyFlows.automation.description.PRICE_CHANGE_UP;
    case "PRICE_CHANGE_DOWN":
      return window._trans._readyFlows.automation.description.PRICE_CHANGE_DOWN;
    case "LOW_STOCK_PRODUCTS":
      return window._trans._readyFlows.automation.description.LOW_STOCK_PRODUCTS;
    case "NEWEST_PRODUCTS":
      return window._trans._readyFlows.automation.description.NEWEST_PRODUCTS;
    case "ASK_FOR_REVIEW":
      return window._trans._readyFlows.automation.description.ASK_FOR_REVIEW;
    case "BACK_IN_STOCK":
      return window._trans._readyFlows.automation.description.BACK_IN_STOCK;
    case "SMS_SUBSCRIPTION":
      return window._trans._readyFlows.automation.description.SMS_SUBSCRIPTION;
    case "REVIEW_REQUEST":
      return window._trans._readyFlows.automation.description.REVIEW_REQUEST;
    default:
      return "";
  }
};

export function isDateInRange(newFeatureRangeDate?: { start: string; end: string }): boolean {
  if (!newFeatureRangeDate) {
    return false;
  }
  const { start, end } = newFeatureRangeDate;

  if (!isValidDate(start) || !isValidDate(end)) {
    return false;
  }
  const startDate = dayjsWithTz(start).startOf("day");
  const endDate = dayjsWithTz(end).endOf("day");
  const now = dayjsWithTz();

  return now.isBetween(startDate, endDate, undefined, "[]");
}

export function isAccountCreationDateValid(newFeatureRangeDate?: { start: string; end: string }): boolean {
  const accountCreatedDate = dayjsWithTz(parseDate(Number(window.EDRONE_CONTEXT.tracker.creationDate)));
  return !accountCreatedDate.isAfter(newFeatureRangeDate?.start);
}

export function isNewFeatureActive(newFeatureRangeDate?: { start: string; end: string }): boolean {
  return isAccountCreationDateValid(newFeatureRangeDate) && isDateInRange(newFeatureRangeDate);
}

export const shuffleArrayRandomly = <T>(array: T[]): T[] => {
  const shuffledCopy = [...array];

  for (let currentIndex = shuffledCopy.length - 1; currentIndex > 0; currentIndex--) {
    const randomIndex = Math.floor(Math.random() * (currentIndex + 1));
    [shuffledCopy[currentIndex], shuffledCopy[randomIndex]] = [shuffledCopy[randomIndex], shuffledCopy[currentIndex]];
  }

  return shuffledCopy;
};

export const formatPhoneNumberToE164 = (phoneNumber: string): string => {
  let cleanedNumber = phoneNumber.replace(/[\s\-()]/g, "");
  if (cleanedNumber.startsWith("00")) {
    cleanedNumber = "+" + cleanedNumber.slice(2);
  }
  return cleanedNumber;
};

export const isValidPhoneNumber = (phoneNumber: string): boolean => {
  if (!phoneNumber) {
    return false;
  }
  const cleanedNumber = phoneNumber.replace(/[\s\-()]/g, "");
  return /^(\+|00)?[1-9]\d{6,19}$/.test(cleanedNumber);
};

export const hasPhoneNumberPrefix = (phoneNumber: string): boolean => {
  if (!phoneNumber || typeof phoneNumber !== "string") {
    return false;
  }
  return phoneNumber.startsWith("+") || phoneNumber.startsWith("00");
};

export const isValidRegex = (input: string): boolean => {
  if (typeof input !== "string") {
    return false;
  }
  const controlCharMap = {
    "\b": "\\b",
    "\f": "\\f",
    "\n": "\\n",
    "\r": "\\r",
    "\t": "\\t",
    "\v": "\\v",
    "\0": "\\0",
  };

  const sanitized = input.replace(/[\b\f\n\r\t\v\0]/g, (c) => controlCharMap[c]);
  const match = sanitized.match(/^\/([\s\S]*)\/([gimsuy]*)$/);

  try {
    if (match) {
      const [, pattern, flags] = match;

      if (/\(\?[imsux]\)/.test(pattern)) {
        return false;
      }

      new RegExp(pattern, flags);
    } else {
      if (/\(\?[imsux]\)/.test(input)) {
        return false;
      }

      new RegExp(input);
    }

    return true;
  } catch {
    return false;
  }
};

const TAB_VALUE_TO_QUERY_MAPPING: Record<NEWSLETTER_TAB_VALUES, string> = {
  [NEWSLETTER_TAB_VALUES.SENT]: "sent",
  [NEWSLETTER_TAB_VALUES.SCHEDULED]: "scheduled",
  [NEWSLETTER_TAB_VALUES.DRAFTS]: "drafts",
  [NEWSLETTER_TAB_VALUES.ARCHIVED]: "archived",
};

const QUERY_TO_TAB_VALUE_MAPPING: Record<string, NEWSLETTER_TAB_VALUES> = {
  sent: NEWSLETTER_TAB_VALUES.SENT,
  scheduled: NEWSLETTER_TAB_VALUES.SCHEDULED,
  drafts: NEWSLETTER_TAB_VALUES.DRAFTS,
  archived: NEWSLETTER_TAB_VALUES.ARCHIVED,
};

export const convertTabValueToQuery = (tabValue: NEWSLETTER_TAB_VALUES): string => {
  return TAB_VALUE_TO_QUERY_MAPPING[tabValue] || "sent";
};

export const convertQueryToTabValue = (query: string | undefined): NEWSLETTER_TAB_VALUES | undefined => {
  if (!query) {
    return undefined;
  }
  return QUERY_TO_TAB_VALUE_MAPPING[query];
};
